import React, {useState, useEffect, Fragment} from "react"
import Moment from "moment"
import {useDispatch, useSelector} from "react-redux"
import {useAuth0} from "@auth0/auth0-react"

// UI
import {Container} from "@uprise/grid"
import {text} from "@uprise/typography"
import {H4} from "@uprise/headings"
import {P} from "@uprise/text"
import {Alert} from "@uprise/alert"
import {Modal} from "@uprise/modal"

// Internal Dependancies
import Profile from "./Profile"
import Activity from "./Activity"
import {Notes} from "../Notes"
import Bookings from "./Bookings/index.js"

// Slices
import {
	fetchUsersByEmail,
	fetchBookingsByUserEmail,
	userDataSelector,
	clearUserState,
	clearUserDetailsState,
	clearDisableUserState,
	clearUserNotesApiState,
	clearUserNotificationState,
	clearNewUserApiState,
	clearupdateUserApiState,
	updateUserApiState,
	updateUserInfo
} from "features/Users/usersSlice"
import {clearBookingApiState, clearCancelBookingApiState, bookingDataSelector} from "features/Bookings/bookingSlice"
import {authSelector} from "features/Auth/authSlice"

import ComponentWrapper from "components/HOC/ComponentWrapper"
import Preloader from "components/shared/Preloader/Preloader"
import {Alerts} from "./Alerts"

import {
	TabsWrapper,
	Tabs,
	TabLabel,
	Tab,
	ItemContainer,
	FormItem,
	HeaderTableContent,
	ModalContainer,
	HeaderButton
} from "./index.style"
import {fetchEmployerByCode} from "../../Employers/employerSlice"
import EditUserDetails from "./EditUserDetails"

const UserDetails = ({history, match}) => {
	const dispatch = useDispatch()
	const authState = useSelector(authSelector)

	const [activeIndex, setActiveIndex] = useState(0)

	const [openModal, setOpenModal] = useState(false)

	const [isEdit, setEdit] = useState(false)

	const [employerCodeExpiredDate, setEmployerCodeExpiredDate] = useState()

	const {getAccessTokenSilently} = useAuth0()
	const {
		userDetails,
		results,
		tasks,
		profile,
		userDetailsState,
		toggleDashboardApiState,
		userBookingsApiState,
		userNotificationState,
		userDisableUserApiState,
		userNotesApiState,
		updateUserApiState,
		updatedSessionApiState,
		requestSessionsApiState
	} = useSelector(userDataSelector)

	const {cancelBookingApiState, createBookingApiState} = useSelector(bookingDataSelector)

	useEffect(() => {
		const getToken = async () => {
			const authToken = await getAccessTokenSilently()
			const employerCodeDetails = await dispatch(fetchEmployerByCode({code: profile?.employerCode, token: authToken}))
			if(!employerCodeDetails.error){
				setEmployerCodeExpiredDate(employerCodeDetails?.payload?.data?.expiryDate);
			}
		}
		getToken()
	}, [profile?.employerCode])

	useEffect(() => {
		if (match.params.email) {
			;(async () => {
				const token = await getAccessTokenSilently()

				dispatch(fetchUsersByEmail({email: match.params.email, token}))
				dispatch(fetchBookingsByUserEmail({userEmail: match.params.email, token: token}))
			})()
		}
		return () => {
			dispatch(clearUserState())
			dispatch(clearBookingApiState())
			dispatch(clearUserDetailsState())
			dispatch(clearUserNotificationState())
			dispatch(clearNewUserApiState())
			dispatch(clearUserNotesApiState())
			dispatch(clearDisableUserState())
			dispatch(clearupdateUserApiState())
		}
	}, [])

	useEffect(() => {
		if (updateUserApiState.isSuccess) {
			history.push("/users/all")
		}
	}, [updateUserApiState])

	useEffect(() => {
		if (userNotesApiState.isError) {
			setTimeout(() => {
				dispatch(clearUserNotesApiState())
			}, 2000)
		}
	}, [userDetailsState, userNotesApiState, dispatch])

	useEffect(() => {
		const getToken = async () => {
			const authToken = await getAccessTokenSilently()
			dispatch(fetchBookingsByUserEmail({userEmail: profile?.email, token: authToken}))
		}
		getToken()
	}, [profile?.employerCode])

	const _disableEdit = () => {
		setEdit(false)
	}

	const onModalButtonClick = e => {
		e.preventDefault()

		setOpenModal(true)
	}

	const _handleUserUpdateAction = async data => {
		const authToken = await getAccessTokenSilently()

		dispatch(
			updateUserInfo({
				currentEmail: profile.email,
				email: data.email,
				name: data.name,
				phone: data.phoneNumber,
				countryCode: data.countryCode.label,
				token: authToken
			})
		)
		setOpenModal(!openModal)
	}

	return (
		<ComponentWrapper
			menuActiveIndex={3}
			subMenuActiveIndex={11}
			headerTitle={`${profile?.name ? ` User: ${profile?.name}` : ``}`}
			headerRightAlignContent={
				<HeaderTableContent>
					<HeaderButton onClick={onModalButtonClick} title={"Update"} />
				</HeaderTableContent>
			}>

			{profile?.isEmployerCodeExpired && <h4>Employer Code {profile?.employerCode} is expired on {Moment(employerCodeExpiredDate).format("Do MMM YYYY")}</h4>}
			
			<Alerts
				toggleDashboardApiState={toggleDashboardApiState}
				userDetailsState={userDetailsState}
				userBookingsApiState={userBookingsApiState}
				userNotificationState={userNotificationState}
				userNotesApiState={userNotesApiState}
				updateUserApiState={updateUserApiState}
				userDisableUserApiState={userDisableUserApiState}
				cancelBookingApiState={cancelBookingApiState}
				clearCancelBookingApiState={clearCancelBookingApiState}
				createBookingApiState={createBookingApiState}
				updatedSessionApiState={updatedSessionApiState}
				requestSessionsApiState={requestSessionsApiState}
			/>

			<Container className='m-b-6' data-testid='userDetailsData'>
				{userDetailsState.isFetching && <Preloader />}
				{userDetails && !userDetailsState.isError && userDetailsState.isSuccess && (
					<Fragment>
						<TabsWrapper>
							<Tabs>
								<Tab onClick={() => setActiveIndex(0)} active={activeIndex === 0}>
									<TabLabel firstChild={true} active={activeIndex === 0}>
										{activeIndex === 0 ? <H4>Profile</H4> : <P fontSize={text.t8}>Profile</P>}
									</TabLabel>
								</Tab>

								<Tab onClick={() => setActiveIndex(1)} active={activeIndex === 1}>
									<TabLabel active={activeIndex === 1}>
										{activeIndex === 1 ? <H4>Bookings</H4> : <P fontSize={text.t8}>Bookings</P>}
									</TabLabel>
								</Tab>
								<Tab onClick={() => setActiveIndex(2)} active={activeIndex === 2}>
									<TabLabel active={activeIndex === 2}>
										{activeIndex === 2 ? (
											<H4>Uprise Program Results</H4>
										) : (
											<P fontSize={text.t8}>Uprise Program Results</P>
										)}
									</TabLabel>
								</Tab>
								<Tab onClick={() => setActiveIndex(3)} active={activeIndex === 3}>
									<TabLabel active={activeIndex === 3}>
										{activeIndex === 3 ? <H4>Notes</H4> : <P fontSize={text.t8}>Notes</P>}
									</TabLabel>
								</Tab>
							</Tabs>
						</TabsWrapper>
						<ItemContainer>
							<Fragment>
								<FormItem active={activeIndex === 0}>
									{profile ? (
										<Profile
											isEditEnabled={isEdit && activeIndex === 0}
											onEditCancel={_disableEdit}
											data={profile}
										/>
									) : null}
								</FormItem>
								<FormItem active={activeIndex === 1}>
									<Bookings
										isEditEnabled={isEdit && activeIndex === 2}
										onPrevious={() => setActiveIndex(1)}
									/>
								</FormItem>
								<FormItem active={activeIndex === 2}>
									<Activity
										isEditEnabled={isEdit && activeIndex === 2}
										results={results}
										tasks={tasks}
										onPrevious={() => setActiveIndex(1)}
									/>
								</FormItem>
								<FormItem active={activeIndex === 3}>
									<Notes coachId={authState?.data?.id} userEmail={match.params.email} />
								</FormItem>
							</Fragment>
						</ItemContainer>
					</Fragment>
				)}
			</Container>
			<Modal
				isOpen={openModal}
				handleClose={() => setOpenModal(false)}
				padding={"24px"}
				backgroundColor={"#ffffff"}
				width={"70%"}
				scroll={true}
				textAlign='center'>
				<H4>{`Update UserInfo`}</H4>
				<ModalContainer>
					<EditUserDetails
						data={profile}
						onComplete={_handleUserUpdateAction}
						onCancel={() => setOpenModal(false)}
					/>
				</ModalContainer>
			</Modal>
		</ComponentWrapper>
	)
}

export default UserDetails
